import { InternalServerError } from '@sparelabs/error-types'
import { Logger } from '@sparelabs/logging'
import { MINUTE } from '@sparelabs/time'
import { isNumber } from 'lodash'
import { BoardingTimeCalculator } from '../boardingTime'
import { FlagDownConstants } from '../flagDown'
import { IWindow } from '../scheduling'
import {
  ArriveByPickupConstraintType,
  MatchingEndpoint,
  RequestIntentType,
  ScheduledRequestFlexWindowType,
} from '../types'
import {
  IConstraintMatchedEstimateInput,
  IConstraintMatchedRequestInput,
  IConstraintServiceInput,
  IConstraintUnmatchedEstimateInput,
  IConstraintUnmatchedRequestInput,
  IPreMatchConstraintInput,
  IRequestConstraints,
} from './RequestConstraintsTypes'
import { RequestSemantics } from './RequestSemantics'

/**
 * Fixed constraints can perform poorly under changing traffic or other weird conditions
 * always add a buffer of 1 minute to ensure these issues don't happen
 */
export const FIXED_TIME_CONSTRAINT_BUFFER = 1 * MINUTE

/**
 * This class builds request constraints from scratch:
 *
 * IRequestConstraints {
 *   pickupWindow: IWindow | null
 *   dropoffWindow: IWindow | null
 *   maxTimeInVehicle: number
 * }
 *
 * For LeaveAt requests:
 * - the pickupWindow is defined
 * - the dropoffWindow is null
 *
 * For ArriveBy requests:
 * - the pickupWindow can be defined (two windows) or null (single window)
 * - the dropoffWindow is defined
 *
 * The class includes methods to build constraints for matched and unmatched requests.
 */
export class RequestConstraintsBuilder {
  private static readonly logger = Logger.build(RequestConstraintsBuilder.name)

  public static AUTO_WINDOW_ARRIVE_BY_DESIRED_BUFFER = 5 * MINUTE
  public static MANUAL_WINDOW_ARRIVE_BY_DESIRED_BUFFER = 30 * MINUTE

  public static buildForUnmatched(
    estimate: IConstraintUnmatchedEstimateInput,
    request: IConstraintUnmatchedRequestInput,
    service: IConstraintServiceInput,
    pickupConstraintInput: IPreMatchConstraintInput,
    excludeWalkingTimeInDesiredTs: boolean,
    scheduledRequestFlexWindowType: ScheduledRequestFlexWindowType,
  ): IRequestConstraints {
    switch (estimate.intentType) {
      case RequestIntentType.LeaveAt:
        return this.buildForLeaveAtUnmatched(
          estimate,
          request,
          pickupConstraintInput,
          excludeWalkingTimeInDesiredTs,
          scheduledRequestFlexWindowType,
        )
      case RequestIntentType.ArriveBy:
        return this.buildForArriveByUnmatched(
          estimate,
          request,
          service,
          pickupConstraintInput.maxDropoffWalkingDuration,
          scheduledRequestFlexWindowType,
        )
    }
  }

  public static buildForMatched(
    estimate: IConstraintMatchedEstimateInput,
    request: IConstraintMatchedRequestInput,
    service: IConstraintServiceInput,
  ): IRequestConstraints {
    switch (estimate.intentType) {
      case RequestIntentType.LeaveAt:
        return this.buildForLeaveAtMatched(estimate, request, service)
      case RequestIntentType.ArriveBy:
        return this.buildForArriveByMatched(estimate, request, service)
    }
  }

  private static buildForLeaveAtUnmatched(
    estimate: IConstraintUnmatchedEstimateInput,
    request: IConstraintUnmatchedRequestInput,
    pickupConstraintInput: Pick<IPreMatchConstraintInput, 'maxPickupWalkingDuration' | 'estimateCreatedTs'>,
    excludeWalkingTimeInDesiredTs: boolean,
    scheduledRequestFlexWindowType: ScheduledRequestFlexWindowType,
  ): IRequestConstraints {
    const { estimateCreatedTs, maxPickupWalkingDuration } = pickupConstraintInput

    const desiredPickupTs = this.getDesiredPickupTsForLeaveAtUnmatched(
      estimate.requestedPickupTs,
      scheduledRequestFlexWindowType,
      excludeWalkingTimeInDesiredTs,
      estimateCreatedTs,
      maxPickupWalkingDuration,
    )

    /**
     * Flagdown requests get an extra high detour flexibility when they are initially matched,
     * just to ensure a high match rate for flagdowns.
     */
    const detourFlexibility = RequestSemantics.isFlagDownRequest(request)
      ? FlagDownConstants.FLAG_DOWN_PRE_MATCH_TRAVEL_DURATION_FLEXIBILITY
      : request.travelDurationFlexibility

    return {
      pickupWindow: {
        min: desiredPickupTs - estimate.searchFlexBackward,
        desired: desiredPickupTs,
        max: desiredPickupTs + estimate.searchFlexForward,
      },
      dropoffWindow: null,
      maxTimeInVehicle: request.travelDuration + detourFlexibility,
    }
  }

  /**
   * We make two adjustments to the desired pickup time:
   *  1. Offset by the time it takes to walk from the rider's origin to their pickup location. This is
   *     done because we assume that the LeaveAt time refers to the time they want to leave from their
   *     point of origin, rather than the time they want to be boarding the vehicle.
   *  2. If it is in the past, assume that the rider wants a trip as soon as possible. Such a discrepancy
   *     may arise due to rematching (e.g. on duty cancellation), or simply due to delay between the
   *     time the estimate was created, and the first time the request gets matched. We make this change for variable window
   *     services only, since most users of consistent windows care to preserve the OTP window and can use overrides to make these trips fit.
   */
  private static getDesiredPickupTsForLeaveAtUnmatched(
    requestedPickupTs: number,
    scheduledRequestFlexWindowType: ScheduledRequestFlexWindowType,
    excludeWalkingTimeInDesiredTs: boolean,
    estimateCreatedTs: number,
    maxPickupWalkingDuration: number,
  ): number {
    switch (scheduledRequestFlexWindowType) {
      case ScheduledRequestFlexWindowType.ManualWithNegotiation:
        return excludeWalkingTimeInDesiredTs ? requestedPickupTs : requestedPickupTs + maxPickupWalkingDuration
      case ScheduledRequestFlexWindowType.AutomaticUsingSearchFlex:
        return excludeWalkingTimeInDesiredTs
          ? Math.max(estimateCreatedTs + maxPickupWalkingDuration, requestedPickupTs)
          : Math.max(estimateCreatedTs, requestedPickupTs) + maxPickupWalkingDuration
    }
  }

  private static buildForArriveByUnmatched(
    estimate: IConstraintUnmatchedEstimateInput,
    request: IConstraintUnmatchedRequestInput,
    service: IConstraintServiceInput,
    maxDropoffWalkingDuration: number,
    scheduledRequestFlexWindowType: ScheduledRequestFlexWindowType,
  ): IRequestConstraints {
    const isManualWindow = scheduledRequestFlexWindowType === ScheduledRequestFlexWindowType.ManualWithNegotiation
    const desiredBuffer = Math.min(
      isManualWindow ? this.MANUAL_WINDOW_ARRIVE_BY_DESIRED_BUFFER : this.AUTO_WINDOW_ARRIVE_BY_DESIRED_BUFFER,
      estimate.searchFlexBackward,
    )
    /**
     * Offset the entire absolute dropoff range by the walking duration from their dropoff location to their final destination.
     * This assumes that estimate.requestedDropoffTs corresponds to when the rider wants to arrive at their final destination.
     */
    const anchorDropoffTs = estimate.requestedDropoffTs - maxDropoffWalkingDuration
    const dropoffWindow: IWindow = {
      min: anchorDropoffTs - estimate.searchFlexBackward,
      desired: anchorDropoffTs - desiredBuffer,
      max: anchorDropoffTs + estimate.searchFlexForward,
    }

    switch (estimate.arriveByPickupConstraintType ?? ArriveByPickupConstraintType.Detour) {
      case ArriveByPickupConstraintType.Detour:
        return {
          pickupWindow: null,
          dropoffWindow,
          maxTimeInVehicle: request.travelDuration + request.travelDurationFlexibility,
        }
      case ArriveByPickupConstraintType.Window: {
        return this.buildForArriveByWithTwoWindows(
          dropoffWindow,
          anchorDropoffTs,
          request.travelDuration,
          request.travelDurationFlexibility,
          request.isRecurring ? service.recurringTripArriveByWindowsBuffer : service.arriveByWindowsBuffer,
          BoardingTimeCalculator.evaluateRequestBoardingTime(service, request, this.logger),
          estimate.arriveByPickupConstraintFlex,
          estimate.searchFlexBackward,
        )
      }
    }
  }

  private static buildForLeaveAtMatched(
    estimate: Pick<IConstraintMatchedEstimateInput, 'endpoint' | 'requestedPickupTs'>,
    request: IConstraintMatchedRequestInput,
    service: IConstraintServiceInput,
  ): IRequestConstraints {
    const isNextAvailableForNow =
      service.buildNextAvailableRequestsForNow && estimate.endpoint === MatchingEndpoint.NextAvailable
    const isManualWindow =
      estimate.endpoint === MatchingEndpoint.Scheduled &&
      service.scheduledRequestFlexWindowType === ScheduledRequestFlexWindowType.ManualWithNegotiation
    const isRecurring = RequestSemantics.isRecurringRequest(request)

    const desiredPickupTs =
      isNextAvailableForNow || isManualWindow || isRecurring
        ? estimate.requestedPickupTs
        : this.assertInitialScheduledTs(request.initialScheduledPickupTs, request.id)
    const anchorPickupTs =
      isManualWindow || isRecurring
        ? estimate.requestedPickupTs
        : this.assertInitialScheduledTs(request.initialScheduledPickupTs, request.id)

    const pickupWindow: IWindow = {
      min: desiredPickupTs - request.acceptedFlexBackward,
      desired: desiredPickupTs,
      max: anchorPickupTs + request.acceptedFlexForward,
    }

    return {
      pickupWindow,
      dropoffWindow: null,
      maxTimeInVehicle: request.travelDuration + request.travelDurationFlexibility,
    }
  }

  private static buildForArriveByMatched(
    estimate: Pick<IConstraintMatchedEstimateInput, 'requestedDropoffTs'>,
    request: IConstraintMatchedRequestInput,
    service: Pick<
      IConstraintServiceInput,
      | 'scheduledRequestFlexWindowType'
      | 'id'
      | 'baseBoardingTime'
      | 'accessibilityFeatures'
      | 'arriveByWindowsBuffer'
      | 'recurringTripArriveByWindowsBuffer'
    >,
  ): IRequestConstraints {
    const isManualWindow =
      service.scheduledRequestFlexWindowType === ScheduledRequestFlexWindowType.ManualWithNegotiation
    const isRecurring = RequestSemantics.isRecurringRequest(request)
    const desiredBuffer = Math.min(
      isManualWindow ? this.MANUAL_WINDOW_ARRIVE_BY_DESIRED_BUFFER : this.AUTO_WINDOW_ARRIVE_BY_DESIRED_BUFFER,
      request.acceptedFlexBackward,
    )

    const anchorDropoffTs =
      isManualWindow || isRecurring
        ? estimate.requestedDropoffTs
        : this.assertInitialScheduledTs(request.initialScheduledDropoffTs, request.id)
    const desiredDropoffTs = isManualWindow || isRecurring ? anchorDropoffTs - desiredBuffer : anchorDropoffTs

    const dropoffWindow: IWindow = {
      min: anchorDropoffTs - request.acceptedFlexBackward,
      desired: desiredDropoffTs,
      max: anchorDropoffTs + request.acceptedFlexForward,
    }

    switch (request.arriveByPickupConstraintType ?? ArriveByPickupConstraintType.Detour) {
      case ArriveByPickupConstraintType.Detour:
        return {
          pickupWindow: null,
          dropoffWindow,
          maxTimeInVehicle: request.travelDuration + request.travelDurationFlexibility,
        }
      case ArriveByPickupConstraintType.Window:
        return this.buildForArriveByWithTwoWindows(
          dropoffWindow,
          anchorDropoffTs,
          request.travelDuration,
          request.travelDurationFlexibility,
          isRecurring ? service.recurringTripArriveByWindowsBuffer : service.arriveByWindowsBuffer,
          BoardingTimeCalculator.evaluateRequestBoardingTime(service, request, this.logger),
          request.arriveByPickupConstraintFlex,
          request.acceptedFlexBackward,
        )
    }
  }

  private static buildForArriveByWithTwoWindows(
    dropoffWindow: IWindow,
    anchorDropoffTs: number,
    travelDuration: number,
    travelDurationFlexibility: number,
    selectedArriveByWindowsBuffer: number,
    boardingTime: number,
    pickupConstraintFlex: number | null,
    acceptedFlexBackward: number,
  ): IRequestConstraints {
    // Set dropoff desired to the middle of the window to promote pooling
    const adjustedDropoffWindow = {
      min: dropoffWindow.min,
      desired: Math.round((dropoffWindow.min + dropoffWindow.max) / 2),
      max: dropoffWindow.max,
    }

    const anchorPickupTs = anchorDropoffTs - travelDuration - 2 * boardingTime - selectedArriveByWindowsBuffer
    const backwardsFlex = Math.min(pickupConstraintFlex ?? Infinity, acceptedFlexBackward)
    const min = anchorPickupTs - backwardsFlex

    // Set pickup desired to the start of the window to promote pooling
    return {
      pickupWindow: {
        min,
        desired: min,
        max: anchorPickupTs,
      },
      dropoffWindow: adjustedDropoffWindow,
      maxTimeInVehicle: travelDuration + travelDurationFlexibility,
    }
  }

  private static assertInitialScheduledTs(desiredTs: number | null | undefined, requestId: string): number {
    if (!isNumber(desiredTs)) {
      throw new InternalServerError('Building matched constraints without initial scheduled timestamp', { requestId })
    }
    return desiredTs
  }
}
